import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Trophy } from "lucide-react";
import api from "@/lib/api";
import { REACTIONS } from "@/components/reactions";

/** Highlights the top reactor-receiver across the past 7 days. Renders a
 *  compact gradient card suitable for the sidebar or homepage strip. */
export default function UserOfTheWeek() {
  const [winner, setWinner] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/users/of_the_week")
      .then(({ data }) => { if (alive) setWinner(data?.winner || null); })
      .catch(() => { if (alive) setWinner(null); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded) return null;
  if (!winner) return null;

  // Top 3 reaction kinds for this user
  const top3 = REACTIONS
    .map((r) => ({ ...r, count: winner.counts?.[r.kind] || 0 }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return (
    <Link
      to={`/u/${winner.user_id}`}
      data-testid="user-of-the-week"
      className="block rounded-2xl p-3 transition-transform hover:scale-[1.01]"
      style={{
        background: "linear-gradient(135deg, rgba(255,42,109,0.18) 0%, rgba(157,76,221,0.16) 100%)",
        border: "1px solid rgba(255,42,109,0.35)",
        boxShadow: "0 6px 24px rgba(157,76,221,0.18)",
      }}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--tt-yellow)] mb-2">
        <Crown className="w-3 h-3" /> User of the Week
      </div>
      <div className="flex items-center gap-3">
        {winner.picture ? (
          <img
            src={winner.picture}
            alt=""
            className="w-12 h-12 rounded-full object-cover border-2 border-[var(--tt-yellow)]"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-base font-extrabold border-2 border-[var(--tt-yellow)]">
            {(winner.name || "?")[0].toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display font-extrabold text-base truncate">{winner.name}</div>
          <div className="flex items-center gap-1 text-[11px] text-white/80">
            <Trophy className="w-3 h-3 text-[var(--tt-yellow)]" />
            <span className="font-mono-tt font-bold">{winner.total}</span>
            <span className="text-[var(--tt-text-muted)]">trophies · 7d</span>
          </div>
        </div>
      </div>
      {top3.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/10">
          {top3.map((r) => (
            <span key={r.kind} className="inline-flex items-center gap-0.5 text-[10px] font-mono-tt text-white/85">
              <img src={r.url} alt="" className="w-3.5 h-3.5" />
              {r.count}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}
